import { useContext, useEffect, useState } from 'react'
import { Button, TextField, Box, MenuItem, Dialog } from '@mui/material'
import { addOrUpdateUser } from 'api'
import {
  companies,
  Company,
  SnackbarSeverity,
  UserRole,
  userRoles
} from 'constant'
import { UserContext } from 'contexts/UserContextProvider'
import { SnackContext } from 'contexts/SnackContextProvider'

enum FormAction {
  Create = 'create',
  Edit = 'edit'
}

interface Props {
  openUserForm: boolean
  setOpenUserForm: (open: boolean) => void
  formAction: string
}

const EditUserForm = ({ openUserForm, setOpenUserForm, formAction }: Props) => {
  const { users, setUsers, selectedUser, setSelectedUser } =
    useContext(UserContext)
  const { handleOpenSnackbar } = useContext(SnackContext)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>(UserRole.Admin.value)
  const [company, setCompany] = useState<string>(Company.QTE)

  useEffect(() => {
    if (formAction === FormAction.Edit && selectedUser) {
      setUsername(selectedUser.username || '')
      setEmail(selectedUser.email || '')
      setRole(selectedUser.role || UserRole.Admin)
      setCompany(selectedUser.company || Company.QTE)
      setPassword('')
    } else {
      setUsername('')
      setEmail('')
      setRole(UserRole.Admin.value)
      setCompany(Company.QTE)
      setPassword('')
    }
  }, [selectedUser, formAction, openUserForm])

  const handleAddOrUpdateUser = async () => {
    try {
      const result: any = await addOrUpdateUser(
        username,
        password,
        email,
        role,
        company
      )
      if (result.data.success) {
        const newUser = result.data.user
        const normalized = { ...newUser, id: newUser.id ?? newUser._id }
        if (formAction === FormAction.Edit) {
          setUsers(
            users.map(u =>
              u.username === normalized.username ? normalized : u
            )
          )
          handleOpenSnackbar(
            SnackbarSeverity.Success,
            'User edited successfully'
          )
        } else {
          setUsers([...users, normalized])
          handleOpenSnackbar(
            SnackbarSeverity.Success,
            'User created successfully'
          )
        }
      } else {
        handleOpenSnackbar(
          SnackbarSeverity.Error,
          formAction === FormAction.Edit
            ? 'User edit failed.'
            : 'User create failed.'
        )
      }
    } catch (error) {
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        formAction === FormAction.Edit
          ? 'User edit failed.'
          : 'User create failed.'
      )
    } finally {
      setOpenUserForm(false)
    }
  }

  return (
    <Dialog
      maxWidth='lg'
      fullWidth
      open={openUserForm}
      onClose={() => {
        setSelectedUser(null)
        setOpenUserForm(false)
      }}
    >
      <Box
        component='form'
        onSubmit={e => {
          e.preventDefault()
          handleAddOrUpdateUser()
        }}
        sx={{ padding: '100px' }}
      >
        <TextField
          label='Username'
          disabled={formAction === FormAction.Edit}
          fullWidth
          required
          value={username}
          onChange={e => setUsername(e.target.value)}
          sx={{ mb: 3 }}
          slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
        />
        <TextField
          label='Email'
          fullWidth
          type='email'
          required={formAction === FormAction.Create}
          value={email}
          onChange={e => setEmail(e.target.value)}
          sx={{ mb: 3 }}
          slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
        />
        <TextField
          label='Password'
          fullWidth
          type='password'
          required={formAction === FormAction.Create}
          value={password}
          onChange={e => setPassword(e.target.value)}
          sx={{ mb: 3 }}
          slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
        />
        <TextField
          label='Role'
          fullWidth
          select
          required={formAction === FormAction.Create}
          value={role}
          onChange={e => setRole(e.target.value)}
          sx={{ mb: 4 }}
        >
          {userRoles.map((role: { label: string; value: string }) => (
            <MenuItem key={role.value} value={role.value}>
              {role.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label='Company'
          fullWidth
          select
          required={formAction === FormAction.Create}
          value={company}
          onChange={e => setCompany(e.target.value)}
          sx={{ mb: 4 }}
        >
          {companies.map((c: string) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button
            variant='outlined'
            onClick={() => setOpenUserForm(false)}
            sx={{ mr: 2, padding: '10px 30px', minWidth: '200px' }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            type='submit'
            sx={{ minWidth: '200px', padding: '10px 30px' }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}

export default EditUserForm
